import { FC, useCallback, useEffect, useState } from "react";
import { ISlide } from "../../../interfaces/ISlide";
import imageSliderController from "../../../Api/imageSliderController";
import LoadingAnimation from "../../../components/_common/Loading";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../hooks/useUser";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useModal } from "../../../hooks/useModal";
import ImageSliderForm from "../../../components/forms/imageSlider/ImageSliderForm/ImageSliderForm";
import { Box, Button } from "@mui/material";
import { baseURL } from "../../../config";

const BASE_URL = baseURL;

interface HomePageGalleryProps { }

const sliderWrapperStyle: React.CSSProperties = {
    maxWidth: "100%",
    margin: "40px auto",
    position: "relative",
};

const imageStyle: React.CSSProperties = {
    width: "100%",
    height: "750px",
    maxHeight: "55vh",
    objectFit: "contain",
    borderRadius: "16px",
    display: "block",
};

const captionStyle: React.CSSProperties = {
    textAlign: "center",
    margin: "12px 0 0 0",
    fontWeight: 500,
    fontSize: "1.1rem",
};

const ArrowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: "44px",
    height: "44px",
    background: "rgba(255, 255, 255, 0.58)",
    borderRadius: "50%",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    zIndex: 2,
    cursor: "pointer",
    border: "none",
    outline: "none",
    transition: "background 0.2s",
};

const NextArrow = (props: any) => (
    <button
        type="button"
        style={{ ...ArrowStyle, right: 16 }}
        onClick={props.onClick}
        aria-label="Następny slajd"
    >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M8 4l8 8-8 8" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </button>
);

const PrevArrow = (props: any) => (
    <button
        type="button"
        style={{ ...ArrowStyle, left: 16 }}
        onClick={props.onClick}
        aria-label="Poprzedni slajd"
    >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M16 4l-8 8 8 8" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </button>
);

const HomePageGallery: FC<HomePageGalleryProps> = () => {
    const [images, setImages] = useState<ISlide[] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const modal = useModal()
    const navigate = useNavigate();
    const user = useUser();
    const isAdmin = user.isAuth;

    const handleEditSlider = () => {
        modal.open(
            <ImageSliderForm />
        )
    };

    const getSlides = useCallback(async () => {
        setLoading(true);
        const res = await imageSliderController.getAllImages();

        if (!res.ok || !res.images) return setLoading(false);
        const images = res.images;
        setImages(images);
        setLoading(false);
    }, []);

    useEffect(() => {
        getSlides();
    }, [getSlides]);

    if (loading) return <LoadingAnimation />;

    if (!images || images.length === 0) return (
        <>
            {isAdmin ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Button
                        sx={{
                            width: "50%",
                            height: "150px",
                            m: 2
                        }}
                        color="success"
                        onClick={handleEditSlider}
                    >
                        Edytowanie zdjęć
                    </Button>
                </Box>
            ) : (
                <div>Brak zdjęć do wyświetlenia.</div>
            )}
        </>
    )

    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        autoplay: true,
        autoplaySpeed: 3000,
        adaptiveHeight: true,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
    };

    return (
        <div style={sliderWrapperStyle}>
            <Slider {...settings}>
                {images.map((img) => (
                    <div
                        key={img._id}
                        style={{
                            borderRadius: "16px",
                            overflow: "hidden"
                        }}
                    >
                        <img
                            src={BASE_URL + img.image}
                            alt={img.name || "slide"}
                            style={{
                                ...imageStyle,
                                cursor: img.link ? "pointer" : "default"
                            }}
                            onClick={() => {
                                if (img.link) {
                                    navigate(img.link);
                                }
                            }}
                        />
                        {img.name && <p style={captionStyle}>{img.name}</p>}
                    </div>
                ))}
            </Slider>
            {isAdmin && (
                <Tooltip title="Edytuj slider">
                    <IconButton
                        color="primary"
                        aria-label="Edytuj slider"
                        onClick={handleEditSlider}
                        sx={{
                            position: "absolute",
                            right: 16,
                            bottom: 16,
                            zIndex: 10,
                            background: "rgba(255,255,255,0.7)",
                            "&:hover": { background: "rgba(25, 118, 210, 0.15)" },
                            boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                            width: 40,
                            height: 40,
                            p: 0,
                        }}
                    >
                        <EditIcon fontSize="medium" />
                    </IconButton>
                </Tooltip>
            )}
        </div>
    );
};

export default HomePageGallery;